import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useLocation, useSearchParams } from "react-router-dom";
import LegalAppLink from "../../components/LegalAppLink";
import { usePagesTranslation } from "../../locales";
import {
    DATA_DELETION_APP_LABELS,
    readDataDeletionAppId,
    readDataDeletionPlatform,
} from "./dataDeletionRequestRoutes";
import DeletionStatusService from "./DeletionStatusService";
import { shouldUseLegalAppShell } from "../../../config/appWebViewLegal";
import DataDeletionFormLayout from "./DataDeletionFormLayout";

const VALID_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function resolveRequestErrorMessage(err, t) {
    const message = err?.message || "";
    if (message === "no user") {
        return t("core.legal.dataDeletionRequest.noUser");
    }
    if (message === "Email is required") {
        return t("core.legal.dataDeletionRequest.enterEmail");
    }
    if (message === "Invalid email address") {
        return t("core.legal.dataDeletionRequest.invalidEmail");
    }
    if (message && message !== "Failed to fetch") {
        return message;
    }
    return t("core.legal.dataDeletionRequest.error");
}

function DataDeletionRequestMain() {
    const { t } = usePagesTranslation();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const platform = readDataDeletionPlatform(searchParams);
    const appId = readDataDeletionAppId(searchParams);
    const appLabel = appId ? DATA_DELETION_APP_LABELS[appId] : null;
    const hideAppChrome = shouldUseLegalAppShell(
        location.pathname,
        location.search,
        location.hash,
    );

    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const trimmedEmail = email.trim();
    const isValidEmail = VALID_EMAIL.test(trimmedEmail);

    const handleRequest = async () => {
        if (!trimmedEmail) {
            setError(t("core.legal.dataDeletionRequest.enterEmail"));
            setSubmitted(false);
            return;
        }
        if (!isValidEmail) {
            setError(t("core.legal.dataDeletionRequest.invalidEmail"));
            setSubmitted(false);
            return;
        }

        setLoading(true);
        setError(null);
        setSubmitted(false);

        try {
            await DeletionStatusService.requestDataDeletionByEmail(trimmedEmail, {
                platform: platform || undefined,
                appId: appId || undefined,
            });
            setSubmitted(true);
        } catch (err) {
            setError(resolveRequestErrorMessage(err, t));
            setSubmitted(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <DataDeletionFormLayout>
            {!hideAppChrome ? (
                <LegalAppLink to="/legal#privacy" sx={{ display: "inline-block", mb: 2 }}>
                    {t("core.legal.dataDeletionRequest.backToPrivacy")}
                </LegalAppLink>
            ) : null}
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                {t("core.legal.dataDeletionRequest.title")}
            </Typography>
            {appLabel ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {appLabel}
                </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("core.legal.dataDeletionRequest.description")}
            </Typography>

            {!submitted ? (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "flex-start",
                            justifyContent: "flex-end",
                            mb: 3,
                        }}
                    >
                        <TextField
                            label={t("core.legal.dataDeletionRequest.email")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t("core.legal.dataDeletionRequest.emailPlaceholder")}
                            type="email"
                            autoComplete="email"
                            sx={{ flex: 1, minWidth: 0 }}
                            variant="outlined"
                            size="small"
                            onKeyDown={(e) =>
                                e.key === "Enter" && isValidEmail && handleRequest()
                            }
                            helperText={
                                trimmedEmail && !isValidEmail
                                    ? t("core.legal.dataDeletionRequest.invalidEmail")
                                    : null
                            }
                            error={!!(trimmedEmail && !isValidEmail)}
                        />
                        <Button
                            variant="contained"
                            onClick={handleRequest}
                            disabled={loading || !isValidEmail}
                            sx={{
                                flexShrink: 0,
                                backgroundColor: "#3BA666",
                                "&:hover": {
                                    backgroundColor: "#2E8B57",
                                },
                                "&.Mui-disabled": {
                                    backgroundColor: "#ccc",
                                    color: "#666",
                                },
                            }}
                        >
                            {loading
                                ? t("core.legal.dataDeletionRequest.submitting")
                                : t("core.legal.dataDeletionRequest.request")}
                        </Button>
                    </Box>

                    {error && (
                        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}
                </>
            ) : (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        backgroundColor: "#e8f5e9",
                        borderColor: "#4caf50",
                    }}
                >
                    <Typography variant="body1" paragraph>
                        {t("core.legal.dataDeletionRequest.checkEmailMessage")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("core.legal.dataDeletionRequest.checkEmailHint", {
                            email: trimmedEmail,
                        })}
                    </Typography>
                </Paper>
            )}
        </DataDeletionFormLayout>
    );
}

export default DataDeletionRequestMain;
